<template>
  <div
    v-if="isLoading || recentTemplates.length > 0"
    data-testid="overview-recent-templates"
    class="
      tw-px-2
      tw-pb-2
      tw-mb-2
      tw-border-solid
      tw-border-x-0
      tw-border-t-0
      tw-border-b
      tw-border-sf-slate-lighter-1
    "
  >
    <template v-if="isLoading">
      <SfSkeletonLoader
        v-for="n in 3"
        :key="n"
        class="tw-px-2"
        :width="['70%', '55%', '80%'][n - 1]"
      />
    </template>
    <template v-else>
      <p class="tw-text-xs tw-text-sf-slate tw-mb-2 tw-mt-2 tw-pl-1">
        {{ I18n.t('js.quick_search.recent_templates') }}
      </p>
      <SfList no-margin class="tw-pr-2">
        <SfListItem
          v-for="template in recentTemplates"
          :key="template.id"
          :item-id="String(template.id)"
          variant="md"
          cornered
          :content-extra-classes="['tw-pl-1.5', 'tw-min-w-0']"
          @item-click="onTemplateClick(template)"
        >
          <SfTruncatedLabel :text="template.name" />
          <template #right-content>
            <i
              class="
                fa fa-chevron-right
                tw-invisible
                group-hover:tw-visible
                tw-text-sf-neutral-light-2 tw-text-xs
              "
            />
          </template>
        </SfListItem>
      </SfList>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import SfListItem from '@app/shared/vue/components/sf-list/sf-list-item.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import SfTruncatedLabel from '@app/shared/vue/components/sf-truncated-label.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type { RecentTemplate } from '@app/application/workflow-overview-navigation/api'
import {
  getRecentTemplates,
  invalidateRecentTemplatesCache,
} from '@app/application/workflow-overview-navigation/api'

defineOptions({
  name: 'OverviewNavigationRecentTemplates',
})

const props =
  defineProps<{
    ledgerId: number
    workflowId: number
  }>()

const I18n = useI18n()

const isLoading = ref(true)
const recentTemplates = ref<RecentTemplate[]>([])

onMounted(async () => {
  try {
    const response = await getRecentTemplates({ ledgerId: props.ledgerId })
    if (response?.recentTemplates) {
      recentTemplates.value = response.recentTemplates
    }
  } finally {
    isLoading.value = false
  }
})

function onTemplateClick(template: RecentTemplate): void {
  invalidateRecentTemplatesCache()
  const { Routes, currentFirmId, currentCompanyId } = window.Silverfin
  const path =
    template.templateType === 'account_entry'
      ? Routes.ledger_company_workflow_account_entry_path({
          firm_id: currentFirmId,
          company_id: currentCompanyId,
          ledger_id: props.ledgerId,
          company_workflow_id: props.workflowId,
          account_number: template.accountNumber,
        })
      : Routes.ledger_reconciliation_text_path({
          firm_id: currentFirmId,
          company_id: currentCompanyId,
          ledger_id: props.ledgerId,
          id: template.id,
          _options: true,
        })
  window.Turbolinks.visit(path)
}
</script>
